import { ContactShadows, Float, Html, Sparkles } from "@react-three/drei";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { playTone } from "../lib/audio";
import { useGameStore } from "../store/gameStore";
import {
  calculateBoatPosition,
  calculateTrailerHeading,
  calculateTurnRate,
  normalizeAngle,
  shouldUseReverseCamera,
} from "./driving";
import {
  getFinaleRover,
  getMission,
  type MissionId,
  targetMap,
} from "./missions";

type RoverSnapshot = {
  x: number;
  z: number;
  heading: number;
  trailerHeading: number;
  speed: number;
  steer: number;
};

const startPositions: Record<MissionId, RoverSnapshot> = {
  mow: {
    x: -20,
    z: -17,
    heading: 0.55,
    trailerHeading: 0.55,
    speed: 0,
    steer: 0,
  },
  snow: {
    x: -23,
    z: -2,
    heading: Math.PI / 2,
    trailerHeading: Math.PI / 2,
    speed: 0,
    steer: 0,
  },
  plant: {
    x: -17.5,
    z: -20,
    heading: 0,
    trailerHeading: 0,
    speed: 0,
    steer: 0,
  },
  cargo: {
    x: -22,
    z: -14,
    heading: Math.PI / 2,
    trailerHeading: Math.PI / 2,
    speed: 0,
    steer: 0,
  },
  boat: {
    x: -23,
    z: -15,
    heading: Math.PI / 2,
    trailerHeading: Math.PI / 2,
    speed: 0,
    steer: 0,
  },
  finale: {
    x: -23,
    z: -18,
    heading: 0.65,
    trailerHeading: 0.65,
    speed: 0,
    steer: 0,
  },
};

const keyState = {
  forward: false,
  back: false,
  left: false,
  right: false,
  brake: false,
  cameraLeft: false,
  cameraRight: false,
};

const useKeyboard = () => {
  useEffect(() => {
    const setKey = (event: KeyboardEvent, value: boolean) => {
      if (
        ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", " "].includes(
          event.key,
        )
      )
        event.preventDefault();
      const key = event.key.toLowerCase();
      if (key === "w" || key === "arrowup") keyState.forward = value;
      if (key === "s" || key === "arrowdown") keyState.back = value;
      if (key === "a" || key === "arrowleft") keyState.left = value;
      if (key === "d" || key === "arrowright") keyState.right = value;
      if (key === " ") keyState.brake = value;
      if (key === "q") keyState.cameraLeft = value;
      if (key === "e") keyState.cameraRight = value;
    };
    const down = (event: KeyboardEvent) => setKey(event, true);
    const up = (event: KeyboardEvent) => setKey(event, false);
    const clear = () =>
      Object.assign(keyState, {
        forward: false,
        back: false,
        left: false,
        right: false,
        brake: false,
        cameraLeft: false,
        cameraRight: false,
      });
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    window.addEventListener("blur", clear);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
      window.removeEventListener("blur", clear);
    };
  }, []);
};

export function GameScene() {
  const selected = useGameStore((state) => state.selected);
  const mission = getMission(selected);

  return (
    <div className="game-canvas" aria-hidden="true">
      <Canvas
        shadows
        dpr={[1, 1.75]}
        camera={{ position: [8, 9, -13], fov: 46, near: 0.1, far: 220 }}
        gl={{ antialias: true, powerPreference: "high-performance" }}
      >
        <color attach="background" args={[mission.sky]} />
        <fog attach="fog" args={[mission.sky, 58, 125]} />
        <Suspense fallback={null}>
          <World key={selected} missionId={selected} />
        </Suspense>
      </Canvas>
    </div>
  );
}

function World({ missionId }: { missionId: MissionId }) {
  const mission = getMission(missionId);
  const rover = useRef<RoverSnapshot>({ ...startPositions[missionId] });

  return (
    <>
      <hemisphereLight
        args={[mission.accent, "#17201a", missionId === "snow" ? 1.8 : 2.4]}
      />
      <directionalLight
        castShadow
        color={missionId === "snow" ? "#b9d9ff" : "#fff4d0"}
        intensity={missionId === "snow" ? 2.8 : 3.6}
        position={[-22, 32, -18]}
        shadow-mapSize={[2048, 2048]}
        shadow-camera-left={-48}
        shadow-camera-right={48}
        shadow-camera-top={48}
        shadow-camera-bottom={-48}
        shadow-bias={-0.0002}
      />
      <Ground missionId={missionId} />
      <MissionSet missionId={missionId} rover={rover} />
      <Rover missionId={missionId} snapshot={rover} />
      <SetDressing missionId={missionId} />
      <ContactShadows
        position={[0, 0.03, 0]}
        opacity={0.38}
        scale={70}
        blur={2.6}
        far={18}
      />
      {missionId === "snow" && (
        <Sparkles
          count={110}
          scale={[70, 25, 70]}
          size={1.4}
          speed={0.35}
          color="#eefbff"
        />
      )}
      <FollowCamera rover={rover} />
    </>
  );
}

function Ground({ missionId }: { missionId: MissionId }) {
  const mission = getMission(missionId);
  return (
    <group>
      <mesh receiveShadow rotation-x={-Math.PI / 2}>
        <planeGeometry args={[150, 150, 1, 1]} />
        <meshStandardMaterial
          color={mission.ground}
          roughness={0.96}
          metalness={0.02}
        />
      </mesh>
      <gridHelper
        args={[120, 40, mission.color, mission.color]}
        position={[0, 0.025, 0]}
        material-opacity={0.055}
        material-transparent
      />
      {(missionId === "mow" || missionId === "finale") && (
        <mesh receiveShadow position={[0, 0.035, 0]} rotation-x={-Math.PI / 2}>
          <planeGeometry args={[38, 29]} />
          <meshStandardMaterial color="#315f39" roughness={1} />
        </mesh>
      )}
      {missionId === "plant" && (
        <mesh receiveShadow position={[0, 0.04, 0]} rotation-x={-Math.PI / 2}>
          <planeGeometry args={[42, 38]} />
          <meshStandardMaterial color="#70543a" roughness={1} />
        </mesh>
      )}
      {(missionId === "boat" || missionId === "finale") && (
        <>
          <mesh
            receiveShadow
            position={[-20, 0.08, 25]}
            rotation-x={-Math.PI / 2}
          >
            <planeGeometry args={[14, 18]} />
            <meshStandardMaterial color="#8d8f80" roughness={0.85} />
          </mesh>
          <mesh position={[0, -0.12, 40]} rotation-x={-Math.PI / 2}>
            <planeGeometry args={[150, 30]} />
            <meshStandardMaterial
              color="#218db5"
              roughness={0.18}
              metalness={0.18}
              transparent
              opacity={0.84}
            />
          </mesh>
        </>
      )}
    </group>
  );
}

function Rover({
  missionId,
  snapshot,
}: {
  missionId: MissionId;
  snapshot: React.MutableRefObject<RoverSnapshot>;
}) {
  const group = useRef<THREE.Group>(null);
  const body = useRef<THREE.Group>(null);
  const phase = useGameStore((state) => state.phase);
  const virtualThrottle = useGameStore((state) => state.virtualThrottle);
  const virtualSteer = useGameStore((state) => state.virtualSteer);
  const completed = useGameStore((state) => state.completed);
  const roverMissionId =
    missionId === "finale" ? getFinaleRover(completed) : missionId;
  const mission = getMission(roverMissionId);
  const modelCompleted =
    missionId === "finale" && roverMissionId === "cargo"
      ? Math.max(0, completed - 5)
      : completed;
  const elapsedRef = useRef(0);
  const telemetryRef = useRef(0);
  const collisionCooldown = useRef(0);
  const previousPhase = useRef(phase);
  useKeyboard();

  useEffect(() => {
    if (phase === "playing" && previousPhase.current !== "paused") {
      Object.assign(snapshot.current, startPositions[missionId]);
      elapsedRef.current = 0;
    }
    previousPhase.current = phase;
  }, [phase, missionId, snapshot]);

  useFrame((_, rawDelta) => {
    if (!group.current || !body.current) return;
    const delta = Math.min(rawDelta, 0.05);
    const data = snapshot.current;
    collisionCooldown.current -= delta;
    if (phase === "playing") {
      const keyboardThrottle =
        (keyState.forward ? 1 : 0) - (keyState.back ? 1 : 0);
      const keyboardSteer = (keyState.left ? 1 : 0) - (keyState.right ? 1 : 0);
      const throttle =
        Math.abs(virtualThrottle) > 0 ? virtualThrottle : keyboardThrottle;
      const steer = Math.abs(virtualSteer) > 0 ? virtualSteer : keyboardSteer;
      const loadedSpeedFactor =
        roverMissionId === "cargo" && modelCompleted >= 3 ? 0.82 : 1;
      const targetSpeed =
        throttle >= 0
          ? throttle * mission.maxSpeed * loadedSpeedFactor
          : throttle * mission.maxSpeed * 0.45 * loadedSpeedFactor;
      data.speed = THREE.MathUtils.damp(
        data.speed,
        targetSpeed,
        mission.acceleration,
        delta,
      );
      if (throttle === 0)
        data.speed = THREE.MathUtils.damp(
          data.speed,
          0,
          keyState.brake ? 12 : 1.65,
          delta,
        );
      data.steer = THREE.MathUtils.damp(data.steer, steer, 8, delta);
      data.heading +=
        calculateTurnRate(data.speed, data.steer, mission.steering) * delta;
      if (roverMissionId === "cargo" || roverMissionId === "boat")
        data.trailerHeading = calculateTrailerHeading(
          data.trailerHeading,
          data.heading,
          data.speed,
          delta,
        );
      else data.trailerHeading = data.heading;

      const previousX = data.x;
      const previousZ = data.z;
      data.x += Math.sin(data.heading) * data.speed * delta;
      data.z += Math.cos(data.heading) * data.speed * delta;
      const worldLimit = 40;
      const outsideWorld =
        data.x < -worldLimit ||
        data.x > worldLimit ||
        data.z < -worldLimit ||
        data.z > worldLimit;
      const hitsTree = decorPositions.some(
        ([x, z]) => Math.hypot(data.x - x, data.z - z) < 1.75,
      );
      const hitsHangar =
        data.x > 18 && data.z < -22 && Math.hypot(data.x - 26, data.z + 28) < 7;
      if (outsideWorld || hitsTree || hitsHangar) {
        data.x = THREE.MathUtils.clamp(previousX, -worldLimit, worldLimit);
        data.z = THREE.MathUtils.clamp(previousZ, -worldLimit, worldLimit);
        data.speed *= -0.22;
        if (collisionCooldown.current <= 0) {
          elapsedRef.current += 3;
          useGameStore.getState().notify("СТОЛКНОВЕНИЕ · +3 СЕК");
          collisionCooldown.current = 1.2;
        }
      }
      elapsedRef.current += delta;
      telemetryRef.current += delta;
      if (telemetryRef.current > 0.12) {
        useGameStore
          .getState()
          .setTelemetry(Math.abs(data.speed), elapsedRef.current);
        telemetryRef.current = 0;
      }
    } else {
      data.speed = THREE.MathUtils.damp(data.speed, 0, 5, delta);
    }
    group.current.position.set(data.x, 0.38, data.z);
    group.current.rotation.y = data.heading;
    body.current.rotation.z = THREE.MathUtils.damp(
      body.current.rotation.z,
      -data.steer * Math.min(Math.abs(data.speed) / 8, 1) * 0.08,
      7,
      delta,
    );
    body.current.position.y =
      Math.sin(performance.now() * 0.006) *
      Math.min(Math.abs(data.speed) * 0.004, 0.025);
  });

  return (
    <group ref={group}>
      <group ref={body}>
        <RoverModel
          missionId={roverMissionId}
          completed={modelCompleted}
          snapshot={snapshot}
        />
      </group>
    </group>
  );
}

function Wheel({
  position,
  scale = 1,
}: {
  position: [number, number, number];
  scale?: number;
}) {
  return (
    <group position={position} scale={scale} rotation-z={Math.PI / 2}>
      <mesh castShadow>
        <cylinderGeometry args={[0.48, 0.48, 0.34, 16]} />
        <meshStandardMaterial color="#101412" roughness={0.78} />
      </mesh>
      <mesh>
        <cylinderGeometry args={[0.21, 0.21, 0.352, 12]} />
        <meshStandardMaterial
          color="#89938c"
          metalness={0.65}
          roughness={0.3}
        />
      </mesh>
    </group>
  );
}

function RoverModel({
  missionId,
  completed,
  snapshot,
}: {
  missionId: MissionId;
  completed: number;
  snapshot: React.MutableRefObject<RoverSnapshot>;
}) {
  const mission = getMission(missionId);
  return (
    <group>
      <mesh castShadow position={[0, 0.42, 0]}>
        <boxGeometry args={[2.15, 0.55, 2.75]} />
        <meshStandardMaterial
          color="#202723"
          metalness={0.52}
          roughness={0.38}
        />
      </mesh>
      <mesh castShadow position={[0, 0.86, 0.22]}>
        <boxGeometry args={[1.72, 0.56, 1.72]} />
        <meshStandardMaterial
          color={mission.color}
          metalness={0.28}
          roughness={0.32}
        />
      </mesh>
      <mesh castShadow position={[0, 1.27, 0.35]}>
        <boxGeometry args={[1.18, 0.28, 0.9]} />
        <meshStandardMaterial
          color="#d8e4dc"
          metalness={0.18}
          roughness={0.25}
        />
      </mesh>
      <mesh position={[0, 1.48, 0.35]}>
        <cylinderGeometry args={[0.12, 0.17, 0.18, 12]} />
        <meshStandardMaterial
          color={mission.accent}
          emissive={mission.color}
          emissiveIntensity={1.5}
        />
      </mesh>
      <Wheel position={[-1.02, 0.24, 0.82]} />
      <Wheel position={[1.02, 0.24, 0.82]} />
      <Wheel position={[-1.02, 0.24, -0.88]} />
      <Wheel position={[1.02, 0.24, -0.88]} />
      {missionId === "mow" && <MowerDeck snapshot={snapshot} />}
      {missionId === "snow" && <SnowRotor snapshot={snapshot} />}
      {missionId === "plant" && <PlantingRig snapshot={snapshot} />}
      {(missionId === "cargo" || missionId === "boat") && (
        <ArticulatedTrailer
          missionId={missionId}
          completed={completed}
          snapshot={snapshot}
        />
      )}
      {(missionId === "mow" || missionId === "plant") && (
        <MachineParticles missionId={missionId} snapshot={snapshot} />
      )}
    </group>
  );
}

const snowParticleIds = Array.from(
  { length: 18 },
  (_, index) => `snow-particle-${index}`,
);
const machineParticleIds = Array.from(
  { length: 12 },
  (_, index) => `machine-particle-${index}`,
);

function MowerDeck({
  snapshot,
}: {
  snapshot: React.MutableRefObject<RoverSnapshot>;
}) {
  const blades = useRef<THREE.Group>(null);
  useFrame((_, delta) => {
    if (!blades.current || snapshot.current.speed <= 0.5) return;
    blades.current.children.forEach((blade, index) => {
      blade.rotation.y += delta * (index % 2 ? -13 : 13);
    });
  });
  return (
    <group position={[0, 0.2, 1.85]}>
      <mesh castShadow>
        <boxGeometry args={[3.55, 0.18, 1.2]} />
        <meshStandardMaterial color="#8ef06c" metalness={0.3} />
      </mesh>
      <group ref={blades} position={[0, -0.11, 0]}>
        {[-1.1, 0, 1.1].map((x) => (
          <group key={x} position={[x, 0, 0]}>
            <mesh>
              <cylinderGeometry args={[0.42, 0.42, 0.06, 18]} />
              <meshStandardMaterial color="#171c19" />
            </mesh>
            <mesh position={[0, 0.05, 0]}>
              <boxGeometry args={[0.78, 0.035, 0.09]} />
              <meshStandardMaterial color="#dfff73" metalness={0.55} />
            </mesh>
          </group>
        ))}
      </group>
    </group>
  );
}

function PlantingRig({
  snapshot,
}: {
  snapshot: React.MutableRefObject<RoverSnapshot>;
}) {
  const discs = useRef<THREE.Group>(null);
  useFrame((_, delta) => {
    if (!discs.current) return;
    discs.current.children.forEach((disc) => {
      disc.rotation.x -= snapshot.current.speed * delta * 2.2;
    });
  });
  return (
    <group position={[0, 0.2, -2.05]}>
      <mesh castShadow position={[0, 0.45, 0]}>
        <boxGeometry args={[3.7, 0.85, 1.25]} />
        <meshStandardMaterial color="#ce7437" roughness={0.45} />
      </mesh>
      <group ref={discs}>
        {[-1.25, -0.42, 0.42, 1.25].map((x) => (
          <group key={x} position={[x, -0.08, -0.25]}>
            <mesh rotation-z={Math.PI / 2}>
              <cylinderGeometry args={[0.29, 0.29, 0.16, 14]} />
              <meshStandardMaterial color="#161a17" />
            </mesh>
            <mesh position={[0, 0.24, 0]}>
              <boxGeometry args={[0.08, 0.28, 0.08]} />
              <meshStandardMaterial color="#ffb65d" />
            </mesh>
          </group>
        ))}
      </group>
    </group>
  );
}

function SnowRotor({
  snapshot,
}: {
  snapshot: React.MutableRefObject<RoverSnapshot>;
}) {
  const rotor = useRef<THREE.Group>(null);
  const snow = useRef<THREE.Group>(null);

  useFrame(({ clock }, delta) => {
    const working = snapshot.current.speed > 0.65;
    if (rotor.current && working)
      rotor.current.rotation.x -= delta * (9 + snapshot.current.speed * 1.8);
    if (!snow.current) return;
    snow.current.visible = working;
    snow.current.children.forEach((particle, index) => {
      const t = (clock.elapsedTime * 1.25 + index * 0.073) % 1;
      particle.position.set(
        0.35 + t * 4.8,
        0.55 + Math.sin(t * Math.PI) * 2.35,
        2.05 - t * 1.25 + Math.sin(index * 2.1) * 0.18,
      );
      particle.scale.setScalar(0.45 + (1 - t) * 0.85);
    });
  });

  return (
    <group>
      <group position={[0, 0.34, 1.9]}>
        <mesh castShadow>
          <boxGeometry args={[3.8, 0.86, 0.52]} />
          <meshStandardMaterial
            color="#47798b"
            metalness={0.48}
            roughness={0.3}
          />
        </mesh>
        <group ref={rotor}>
          <mesh rotation-z={Math.PI / 2}>
            <cylinderGeometry args={[0.34, 0.34, 3.48, 14]} />
            <meshStandardMaterial
              color="#eafdff"
              metalness={0.68}
              roughness={0.2}
            />
          </mesh>
          {[-1.35, -0.8, -0.25, 0.3, 0.85, 1.4].map((x, index) => (
            <mesh key={x} position={[x, 0, 0]} rotation-z={index * 1.15}>
              <boxGeometry args={[0.08, 0.82, 0.18]} />
              <meshStandardMaterial
                color="#9ee8ff"
                emissive="#5ec9ff"
                emissiveIntensity={0.55}
              />
            </mesh>
          ))}
        </group>
      </group>
      <group position={[0.75, 1.1, 1.65]} rotation-z={-0.48}>
        <mesh castShadow>
          <cylinderGeometry args={[0.3, 0.42, 1.55, 12]} />
          <meshStandardMaterial
            color="#315866"
            metalness={0.5}
            roughness={0.32}
          />
        </mesh>
        <mesh position={[0, 0.86, 0]} rotation-z={-0.55}>
          <coneGeometry args={[0.48, 0.85, 12]} />
          <meshStandardMaterial color="#5ec9ff" metalness={0.4} />
        </mesh>
      </group>
      <group ref={snow}>
        {snowParticleIds.map((id, index) => (
          <mesh key={id}>
            <sphereGeometry args={[0.13 + (index % 3) * 0.035, 7, 5]} />
            <meshBasicMaterial
              color={index % 2 ? "#ffffff" : "#c9f3ff"}
              transparent
              opacity={0.9}
            />
          </mesh>
        ))}
      </group>
    </group>
  );
}

function ArticulatedTrailer({
  missionId,
  completed,
  snapshot,
}: {
  missionId: "cargo" | "boat";
  completed: number;
  snapshot: React.MutableRefObject<RoverSnapshot>;
}) {
  const trailer = useRef<THREE.Group>(null);
  const boat = missionId === "boat";
  const bedZ = boat ? -3.75 : -3.05;
  const bedLength = boat ? 5.7 : 4.05;

  useFrame((_, delta) => {
    if (!trailer.current) return;
    trailer.current.rotation.y = THREE.MathUtils.damp(
      trailer.current.rotation.y,
      normalizeAngle(
        snapshot.current.trailerHeading - snapshot.current.heading,
      ),
      8,
      delta,
    );
  });

  return (
    <group ref={trailer} position={[0, 0, -1.35]}>
      <mesh castShadow position={[0, 0.5, -1.25]}>
        <boxGeometry args={[0.16, 0.16, 2.5]} />
        <meshStandardMaterial color="#808a84" metalness={0.7} />
      </mesh>
      <mesh castShadow position={[0, 0.55, bedZ]}>
        <boxGeometry args={[3.1, 0.38, bedLength]} />
        <meshStandardMaterial
          color="#333a36"
          metalness={0.52}
          roughness={0.4}
        />
      </mesh>
      <Wheel position={[-1.32, 0.24, bedZ]} scale={0.92} />
      <Wheel position={[1.32, 0.24, bedZ]} scale={0.92} />
      {missionId === "cargo" ? (
        <group position={[0, 0.95, bedZ]}>
          {[0, 1, 2].slice(0, Math.min(completed, 3)).map((crate) => (
            <group
              key={`cargo-crate-${crate}`}
              position={[
                crate === 2 ? 0 : crate === 0 ? -0.62 : 0.62,
                crate === 2 ? 0.88 : 0,
                crate === 2 ? 0.15 : -0.2,
              ]}
            >
              <mesh castShadow>
                <boxGeometry args={[1.05, 0.9, 1.45]} />
                <meshStandardMaterial color="#d7c370" roughness={0.8} />
              </mesh>
              <mesh position={[0, 0, 0.73]}>
                <boxGeometry args={[0.65, 0.07, 0.03]} />
                <meshStandardMaterial
                  color="#f5dd46"
                  emissive="#f5dd46"
                  emissiveIntensity={0.5}
                />
              </mesh>
            </group>
          ))}
        </group>
      ) : (
        <group position={[0, 1.25, bedZ - 0.25]}>
          <mesh castShadow rotation-x={Math.PI / 2}>
            <cylinderGeometry args={[0.5, 1.25, 5.1, 5]} />
            <meshStandardMaterial
              color="#e8f2f2"
              metalness={0.15}
              roughness={0.26}
            />
          </mesh>
          <mesh castShadow position={[0, 0.86, -0.5]}>
            <boxGeometry args={[1.5, 0.75, 1.65]} />
            <meshStandardMaterial
              color="#225b70"
              metalness={0.28}
              roughness={0.25}
            />
          </mesh>
        </group>
      )}
    </group>
  );
}

function MachineParticles({
  missionId,
  snapshot,
}: {
  missionId: "mow" | "plant";
  snapshot: React.MutableRefObject<RoverSnapshot>;
}) {
  const particles = useRef<THREE.Group>(null);
  useFrame(({ clock }) => {
    if (!particles.current) return;
    particles.current.visible = snapshot.current.speed > 0.7;
    particles.current.children.forEach((particle, index) => {
      const t = (clock.elapsedTime * 1.6 + index * 0.11) % 1;
      const side = ((index % 5) - 2) * 0.42;
      particle.position.set(
        side + Math.sin(index * 1.7) * t * 0.35,
        0.22 + Math.sin(t * Math.PI) * 0.55,
        missionId === "mow" ? 1.65 - t * 2.1 : -2.25 - t * 1.45,
      );
      particle.rotation.x += 0.08;
      particle.rotation.z += 0.12;
    });
  });

  return (
    <group ref={particles}>
      {machineParticleIds.map((id) => (
        <mesh key={`${missionId}-${id}`}>
          {missionId === "mow" ? (
            <boxGeometry args={[0.045, 0.2, 0.045]} />
          ) : (
            <dodecahedronGeometry args={[0.08, 0]} />
          )}
          <meshBasicMaterial
            color={missionId === "mow" ? "#8fcc55" : "#b98957"}
            transparent
            opacity={0.78}
          />
        </mesh>
      ))}
    </group>
  );
}

function MissionSet({
  missionId,
  rover,
}: {
  missionId: MissionId;
  rover: React.MutableRefObject<RoverSnapshot>;
}) {
  const phase = useGameStore((state) => state.phase);
  const targets = targetMap[missionId];
  const [hit, setHit] = useState<Set<number>>(() => new Set());
  const hitRef = useRef(hit);
  const previousPhase = useRef(phase);
  const cargoDwell = useRef(0);
  const finalReverseApproach = useRef(false);

  useEffect(() => {
    if (phase === "playing" && previousPhase.current !== "paused") {
      const empty = new Set<number>();
      hitRef.current = empty;
      setHit(empty);
      cargoDwell.current = 0;
      finalReverseApproach.current = false;
    }
    previousPhase.current = phase;
  }, [phase]);

  useFrame((_, rawDelta) => {
    if (phase !== "playing") return;
    const delta = Math.min(rawDelta, 0.05);
    const data = rover.current;
    const sequential =
      missionId === "plant" ||
      missionId === "cargo" ||
      missionId === "boat" ||
      missionId === "finale";
    const availableTargets =
      missionId === "snow" && hitRef.current.size < 24 ? 24 : targets.length;
    const candidates = sequential
      ? [hitRef.current.size]
      : targets.slice(0, availableTargets).map((_, index) => index);
    let changed = false;
    const next = new Set(hitRef.current);
    for (const index of candidates) {
      if (index >= targets.length || next.has(index)) continue;
      const target = targets[index];
      const pointType =
        missionId === "finale"
          ? index < 5
            ? "mow"
            : index < 9
              ? "cargo"
              : "boat"
          : missionId;
      const finalBoatTarget =
        pointType === "boat" && index === targets.length - 1;
      const boatPosition = finalBoatTarget
        ? calculateBoatPosition(
            data.x,
            data.z,
            data.heading,
            data.trailerHeading,
          )
        : null;
      const trackingX = boatPosition?.x ?? data.x;
      const trackingZ = boatPosition?.z ?? data.z;
      const distance = Math.hypot(trackingX - target.x, trackingZ - target.z);
      const radius =
        pointType === "mow"
          ? 2.55
          : pointType === "snow"
            ? 2.25
            : pointType === "plant"
              ? 2.5
              : pointType === "boat"
                ? 3.8
                : 3.25;
      const blockedByPrecision =
        missionId === "plant" && index >= 20 && Math.abs(data.speed) > 4.2;
      if (finalBoatTarget && data.speed < -0.08)
        finalReverseApproach.current = true;
      const blockedByReverse = finalBoatTarget && !finalReverseApproach.current;
      if (
        pointType === "cargo" &&
        distance < radius &&
        Math.abs(data.speed) < 0.65
      ) {
        cargoDwell.current += delta;
        if (cargoDwell.current < 0.85) continue;
        cargoDwell.current = 0;
        next.add(index);
        changed = true;
      } else if (pointType === "cargo") {
        cargoDwell.current = 0;
      } else if (
        distance < radius &&
        !blockedByPrecision &&
        !blockedByReverse
      ) {
        next.add(index);
        changed = true;
      }
    }
    if (changed) {
      hitRef.current = next;
      setHit(next);
      useGameStore.getState().setProgress(next.size);
      playTone(360 + next.size * 8, 0.045, 0.014);
      if (next.size === targets.length) useGameStore.getState().finishMission();
    }
  });

  return (
    <group>
      {missionId === "boat" && (
        <Route targets={targets} color={getMission(missionId).color} />
      )}
      {missionId === "finale" && (
        <Route targets={targets.slice(9)} color={getMission(missionId).color} />
      )}
      {missionId === "mow" && hit.size >= 24 && (
        <SprinklerField rover={rover} />
      )}
      {missionId === "plant" && hit.size >= 20 && <RoughField rover={rover} />}
      {targets.map((target, index) => {
        if (missionId === "snow" && index >= 24 && hit.size < 24) return null;
        const previous = targets[Math.max(0, index - 1)];
        const next = targets[Math.min(targets.length - 1, index + 1)];
        const rotationY =
          missionId === "boat" || (missionId === "finale" && index >= 9)
            ? Math.atan2(next.x - previous.x, next.z - previous.z)
            : 0;
        return (
          <MissionTarget
            key={`${missionId}-${target.x}-${target.z}`}
            missionId={missionId}
            index={index}
            position={[target.x, 0, target.z]}
            rotationY={rotationY}
            hit={hit.has(index)}
            active={
              missionId === "mow" || missionId === "snow" || index === hit.size
            }
          />
        );
      })}
    </group>
  );
}

function MissionTarget({
  missionId,
  index,
  position,
  rotationY,
  hit,
  active,
}: {
  missionId: MissionId;
  index: number;
  position: [number, number, number];
  rotationY: number;
  hit: boolean;
  active: boolean;
}) {
  const mission = getMission(missionId);
  if (missionId === "finale") {
    const stageMission = index < 5 ? "mow" : index < 9 ? "cargo" : "boat";
    const localIndex = index < 5 ? index : index < 9 ? index - 5 : index - 9;
    return (
      <MissionTarget
        missionId={stageMission}
        index={localIndex}
        position={position}
        rotationY={rotationY}
        hit={hit}
        active={active}
      />
    );
  }
  if (missionId === "mow") {
    return (
      <group position={position}>
        <mesh receiveShadow position={[0, hit ? 0.045 : 0.17, 0]}>
          <boxGeometry args={[4.25, hit ? 0.06 : 0.28, 4.25]} />
          <meshStandardMaterial
            color={hit ? "#4f8a4a" : index % 2 ? "#315f39" : "#376a3f"}
            roughness={1}
          />
        </mesh>
        {!hit &&
          Array.from({ length: 7 }, (_, blade) => {
            const angle = blade * 2.399;
            const radius = 0.45 + (blade % 3) * 0.5;
            return (
              <mesh
                key={`blade-${angle}`}
                position={[
                  Math.sin(angle) * radius,
                  0.42,
                  Math.cos(angle) * radius,
                ]}
                rotation-z={(blade % 2 ? 1 : -1) * 0.08}
              >
                <boxGeometry args={[0.05, 0.65, 0.05]} />
                <meshStandardMaterial color="#77aa50" />
              </mesh>
            );
          })}
      </group>
    );
  }
  if (missionId === "snow") {
    return <SnowBank position={position} hit={hit} index={index} />;
  }
  if (missionId === "plant") {
    return (
      <group position={position}>
        <mesh receiveShadow position={[0, 0.07, 0]}>
          <boxGeometry args={[3.4, 0.12, 6.6]} />
          <meshStandardMaterial
            color={hit ? "#402c20" : active ? "#896848" : "#72563c"}
            roughness={1}
            emissive={active ? mission.color : "#000000"}
            emissiveIntensity={active ? 0.12 : 0}
          />
        </mesh>
        {hit &&
          [-1.05, -0.35, 0.35, 1.05].map((x) => (
            <mesh key={x} position={[x, 0.17, 0]}>
              <boxGeometry args={[0.2, 0.16, 6.2]} />
              <meshStandardMaterial color="#2f211a" />
            </mesh>
          ))}
      </group>
    );
  }
  if (missionId === "cargo") {
    const labels = ["ГРУЗ A", "ГРУЗ B", "ГРУЗ C", "СКЛАД"];
    return (
      <group position={position}>
        <mesh receiveShadow position={[0, 0.08, 0]}>
          <cylinderGeometry args={[2.8, 2.8, 0.14, 24]} />
          <meshStandardMaterial
            color={hit ? "#3a413c" : active ? mission.color : "#6d705c"}
            emissive={active ? mission.color : "#000000"}
            emissiveIntensity={active ? 0.32 : 0}
            roughness={0.72}
          />
        </mesh>
        <mesh position={[0, 0.17, 0]} rotation-x={-Math.PI / 2}>
          <ringGeometry args={[2.05, 2.38, 32]} />
          <meshBasicMaterial
            color={active ? mission.accent : "#90978f"}
            transparent
            opacity={hit ? 0.12 : 0.8}
          />
        </mesh>
        {!hit && active && (
          <Float speed={2} rotationIntensity={0} floatIntensity={0.28}>
            <Html center position={[0, 3.1, 0]} distanceFactor={12}>
              <div className="world-label cargo-label">
                <b>{labels[index]}</b>
                <span>ОСТАНОВИТЕСЬ</span>
              </div>
            </Html>
          </Float>
        )}
      </group>
    );
  }
  if (missionId === "boat" && index === 8) {
    return (
      <group position={position}>
        <mesh receiveShadow position={[0, 0.08, 0]}>
          <cylinderGeometry args={[3.2, 3.2, 0.14, 28]} />
          <meshStandardMaterial
            color={hit ? "#4d625e" : mission.color}
            emissive={active ? mission.color : "#000000"}
            emissiveIntensity={active ? 0.35 : 0}
            roughness={0.78}
          />
        </mesh>
        <mesh position={[0, 0.17, 0]} rotation-x={-Math.PI / 2}>
          <ringGeometry args={[2.35, 2.7, 32]} />
          <meshBasicMaterial
            color={mission.accent}
            transparent
            opacity={0.72}
          />
        </mesh>
        {!hit && active && (
          <Float speed={2} rotationIntensity={0} floatIntensity={0.28}>
            <Html center position={[0, 3.1, 0]} distanceFactor={12}>
              <div className="world-label cargo-label">
                <b>РАЗВОРОТ</b>
                <span>ПОДГОТОВЬТЕСЬ К РЕВЕРСУ</span>
              </div>
            </Html>
          </Float>
        )}
      </group>
    );
  }
  if (hit) return null;
  return (
    <group position={position} rotation-y={rotationY}>
      <mesh position={[-3, 1.8, 0]}>
        <boxGeometry args={[0.18, 3.6, 0.18]} />
        <meshStandardMaterial
          color={active ? mission.color : "#768078"}
          emissive={active ? mission.color : "#000"}
          emissiveIntensity={1.6}
        />
      </mesh>
      <mesh position={[3, 1.8, 0]}>
        <boxGeometry args={[0.18, 3.6, 0.18]} />
        <meshStandardMaterial
          color={active ? mission.color : "#768078"}
          emissive={active ? mission.color : "#000"}
          emissiveIntensity={1.6}
        />
      </mesh>
      <mesh position={[0, 3.5, 0]}>
        <boxGeometry args={[6.18, 0.18, 0.18]} />
        <meshStandardMaterial
          color={active ? mission.color : "#768078"}
          emissive={active ? mission.color : "#000"}
          emissiveIntensity={1.6}
        />
      </mesh>
      {active && (
        <Float speed={2} rotationIntensity={0} floatIntensity={0.35}>
          <Html center position={[0, 4.65, 0]} distanceFactor={12}>
            <div className="world-label">
              {missionId === "boat" && index === 9
                ? "R"
                : String(index + 1).padStart(2, "0")}
            </div>
          </Html>
        </Float>
      )}
    </group>
  );
}

function SnowBank({
  position,
  hit,
  index,
}: {
  position: [number, number, number];
  hit: boolean;
  index: number;
}) {
  const bank = useRef<THREE.Group>(null);
  useFrame((_, delta) => {
    if (!bank.current) return;
    const scale = THREE.MathUtils.damp(
      bank.current.scale.x,
      hit ? 0.03 : 1,
      8,
      delta,
    );
    bank.current.scale.setScalar(scale);
    if (hit) bank.current.rotation.y += delta * 2.4;
  });
  return (
    <group ref={bank} position={position}>
      <mesh castShadow position={[0, 0.45, 0]} scale={[1.5, 0.65, 1]}>
        <dodecahedronGeometry args={[1.25, 1]} />
        <meshStandardMaterial
          color={index % 2 ? "#d9ecf2" : "#eef7f8"}
          roughness={0.92}
        />
      </mesh>
    </group>
  );
}

const sprinklerPositions = [
  [-10, -7],
  [9, -5],
  [-7, 8],
  [11, 9],
] as const;

function SprinklerField({
  rover,
}: {
  rover: React.MutableRefObject<RoverSnapshot>;
}) {
  return (
    <group>
      {sprinklerPositions.map(([x, z], index) => (
        <Sprinkler
          key={`${x}-${z}`}
          position={[x, 0, z]}
          rover={rover}
          offset={index * 1.35}
        />
      ))}
    </group>
  );
}

function Sprinkler({
  position,
  rover,
  offset,
}: {
  position: [number, number, number];
  rover: React.MutableRefObject<RoverSnapshot>;
  offset: number;
}) {
  const jet = useRef<THREE.Group>(null);
  useFrame(({ clock }, delta) => {
    if (jet.current) jet.current.rotation.y = clock.elapsedTime * 1.25 + offset;
    const distance = Math.hypot(
      rover.current.x - position[0],
      rover.current.z - position[2],
    );
    if (distance < 3.6)
      rover.current.speed = THREE.MathUtils.damp(
        rover.current.speed,
        0,
        2.8,
        delta,
      );
  });
  return (
    <group position={position}>
      <mesh castShadow position={[0, 0.38, 0]}>
        <cylinderGeometry args={[0.16, 0.22, 0.76, 10]} />
        <meshStandardMaterial color="#2e4840" metalness={0.55} />
      </mesh>
      <group ref={jet} position={[0, 0.82, 0]}>
        <mesh position={[0, 0.18, 2.7]} rotation-x={-0.08}>
          <boxGeometry args={[0.09, 0.09, 5.4]} />
          <meshBasicMaterial
            color="#b9f2ff"
            transparent
            opacity={0.52}
            depthWrite={false}
          />
        </mesh>
        <mesh position={[0, 0, 0]}>
          <sphereGeometry args={[0.23, 10, 8]} />
          <meshStandardMaterial
            color="#d8fbff"
            emissive="#8ce8ff"
            emissiveIntensity={1.2}
          />
        </mesh>
      </group>
    </group>
  );
}

const roughPositions = [
  [-11, 5],
  [-1, -7],
  [9, 7],
] as const;

function RoughField({
  rover,
}: {
  rover: React.MutableRefObject<RoverSnapshot>;
}) {
  useFrame((_, delta) => {
    for (const [x, z] of roughPositions) {
      if (Math.hypot(rover.current.x - x, rover.current.z - z) < 3.4)
        rover.current.speed = THREE.MathUtils.damp(
          rover.current.speed,
          2.8,
          2.2,
          delta,
        );
    }
  });
  return (
    <group>
      {roughPositions.map(([x, z]) => (
        <group key={`${x}-${z}`} position={[x, 0, z]}>
          <mesh receiveShadow position={[0, 0.06, 0]}>
            <cylinderGeometry args={[3.3, 3.3, 0.1, 18]} />
            <meshStandardMaterial color="#4b382a" roughness={1} />
          </mesh>
          {[-1.6, -0.5, 0.8, 1.65].map((offset) => (
            <mesh
              key={offset}
              castShadow
              position={[
                offset,
                0.2 + Math.abs(offset) * 0.03,
                Math.sin(offset * 3) * 1.2,
              ]}
              rotation={[0.2, offset, 0.15]}
            >
              <dodecahedronGeometry
                args={[0.32 + Math.abs(offset) * 0.08, 0]}
              />
              <meshStandardMaterial color="#6a5541" roughness={0.96} />
            </mesh>
          ))}
        </group>
      ))}
    </group>
  );
}

function Route({
  targets,
  color,
}: {
  targets: { x: number; z: number }[];
  color: string;
}) {
  return (
    <group>
      {targets.slice(1).map((target, index) => {
        const previous = targets[index];
        const dx = target.x - previous.x;
        const dz = target.z - previous.z;
        const length = Math.hypot(dx, dz);
        return (
          <mesh
            key={`${target.x}-${target.z}`}
            position={[
              (target.x + previous.x) / 2,
              0.035,
              (target.z + previous.z) / 2,
            ]}
            rotation={[-Math.PI / 2, 0, -Math.atan2(dz, dx)]}
          >
            <planeGeometry args={[length, 1.25]} />
            <meshStandardMaterial
              color={color}
              transparent
              opacity={0.13}
              depthWrite={false}
            />
          </mesh>
        );
      })}
    </group>
  );
}

function FollowCamera({
  rover,
}: {
  rover: React.MutableRefObject<RoverSnapshot>;
}) {
  const { camera } = useThree();
  const lookAt = useMemo(() => new THREE.Vector3(), []);
  const desired = useMemo(() => new THREE.Vector3(), []);
  const reverseBlend = useRef(0);
  const reversing = useRef(false);
  const manualOrbit = useRef(0);
  useFrame((_, delta) => {
    const data = rover.current;
    reversing.current = shouldUseReverseCamera(reversing.current, data.speed);
    reverseBlend.current = THREE.MathUtils.damp(
      reverseBlend.current,
      reversing.current ? 1 : 0,
      2.6,
      delta,
    );
    const orbitInput =
      (keyState.cameraLeft ? 1 : 0) - (keyState.cameraRight ? 1 : 0);
    if (orbitInput !== 0)
      manualOrbit.current = THREE.MathUtils.clamp(
        manualOrbit.current + orbitInput * delta * 1.9,
        -1.25,
        1.25,
      );
    else
      manualOrbit.current = THREE.MathUtils.damp(
        manualOrbit.current,
        0,
        2.8,
        delta,
      );

    // Orbit around the rover instead of crossing through it during the turn.
    const viewHeading =
      data.heading + reverseBlend.current * Math.PI + manualOrbit.current;
    const viewX = Math.sin(viewHeading);
    const viewZ = Math.cos(viewHeading);
    desired.set(data.x - viewX * 11, 7.4, data.z - viewZ * 11);
    camera.position.lerp(desired, 1 - Math.exp(-delta * 4.2));
    lookAt.set(data.x + viewX * 3.5, 0.7, data.z + viewZ * 3.5);
    camera.lookAt(lookAt);
  });
  return null;
}

const decorPositions = [
  [-30, -25],
  [-24, 22],
  [-28, 8],
  [-13, 29],
  [1, 31],
  [18, 27],
  [29, 18],
  [31, 2],
  [27, -18],
  [13, -28],
  [-4, -30],
] as const;

function SetDressing({ missionId }: { missionId: MissionId }) {
  const mission = getMission(missionId);
  return (
    <group>
      {decorPositions.map(([x, z], index) => (
        <group
          key={`${x}-${z}`}
          position={[x, 0, z]}
          scale={0.85 + (index % 3) * 0.18}
        >
          <mesh castShadow position={[0, 1.3, 0]}>
            <cylinderGeometry args={[0.24, 0.34, 2.6, 7]} />
            <meshStandardMaterial color="#5b4434" />
          </mesh>
          <mesh castShadow position={[0, 3, 0]}>
            <coneGeometry args={[1.45, 3.6, 7]} />
            <meshStandardMaterial
              color={missionId === "snow" ? "#b9d5ce" : "#254c38"}
              roughness={0.92}
            />
          </mesh>
          {missionId === "snow" && (
            <mesh position={[0, 3.7, 0]}>
              <coneGeometry args={[1.05, 1.8, 7]} />
              <meshStandardMaterial color="#e9f4f5" roughness={1} />
            </mesh>
          )}
        </group>
      ))}
      <group position={[26, 0, -28]} rotation-y={-0.5}>
        <mesh castShadow position={[0, 2.5, 0]}>
          <boxGeometry args={[14, 5, 8]} />
          <meshStandardMaterial
            color="#252c29"
            roughness={0.55}
            metalness={0.25}
          />
        </mesh>
        <mesh position={[0, 2.4, -4.03]}>
          <boxGeometry args={[5.8, 3.8, 0.12]} />
          <meshStandardMaterial color="#111513" metalness={0.45} />
        </mesh>
        <mesh position={[0, 4.4, -4.12]}>
          <boxGeometry args={[3.2, 0.12, 0.06]} />
          <meshStandardMaterial
            color={mission.color}
            emissive={mission.color}
            emissiveIntensity={2}
          />
        </mesh>
      </group>
      {[
        [-36, 0, 12],
        [37, 0, -4],
      ].map(([x, y, z]) => (
        <group key={`${x}-${z}`} position={[x, y, z]}>
          <mesh castShadow position={[0, 5, 0]}>
            <cylinderGeometry args={[0.18, 0.46, 10, 10]} />
            <meshStandardMaterial
              color="#c3cbc6"
              metalness={0.72}
              roughness={0.28}
            />
          </mesh>
          <mesh position={[0, 9.5, 0]}>
            <sphereGeometry args={[0.35, 12, 8]} />
            <meshStandardMaterial
              color={mission.color}
              emissive={mission.color}
              emissiveIntensity={1.5}
            />
          </mesh>
        </group>
      ))}
    </group>
  );
}
